import {nnTrim} from "../util";

/**
 * Any enum
 */
class Enum {

    /**
     *
     * @param code {Number|String} A unique code that identifies the enum value
     */
    constructor(code) {
        this.code = code;
    }

    getCode() {
        return this.code;
    }

    static _getForCode(enumClass, code) {
        code = nnTrim(code);
        if (!code) {
            return undefined;
        }
        const entries = Object.entries(enumClass);
        for (let e in entries) {
            let entry = entries[e];
            if (nnTrim(entry[1].code) === code)
                return entry[1];
        }
        return undefined;
    }

    static _exists(enumClass, code) {
        return this._getForCode(enumClass, code) !== undefined;
    }

    static _getPossibleCodes(enumClass) {
        let res = [];
        const possible = this._getPossibleValues(enumClass);
        for (let p in possible) {
            res.push(possible[p].getCode());
        }
        return res;
    }

    static _getPossibleValues(enumClass) {
        let res = [];
        const entries = Object.entries(enumClass);
        for (let e in entries) {
            let entry = entries[e];
            res.push(entry[1]);
        }
        return res;
    }
}

/**
 * An enum with a localized name
 * @extends Enum
 */
class LocalizedEnum extends Enum {

    /**
     * @param code {Number|String} A unique code that identifies the enum value
     * @param localizationKey {string} the key for the localized name of the enum value
     */
    constructor(code, localizationKey) {
        super(code);
        this.localizationKey = localizationKey;
    }

    getLocalizedName(translator) {
        return translator.translate(this.localizationKey);
    }

    getLocalizationKey() {
        return this.localizationKey;
    }
}

/**
 * @extends Enum
 * @hideconstructor
 */
class LayerGroupTypeEnum extends Enum {

    /**
     * Classifiers hold children that can be layers or other classifiers
     * @type {LayerGroupTypeEnum}
     */
    static CLASSIFIER = new LayerGroupTypeEnum('CLASSIFIER');

    /**
     * Layers do not hold children and are leafs in the tree
     * @type {LayerGroupTypeEnum}
     */
    static LAYER = new LayerGroupTypeEnum('LAYER');

    static getForCode(code) {
        return Enum._getForCode(LayerGroupTypeEnum, code);
    }

    static exists(code) {
        return Enum._exists(LayerGroupTypeEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(LayerGroupTypeEnum)
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(LayerGroupTypeEnum)
    }
}

/**
 * @extends Enum
 * @hideconstructor
 */
class LayerTypeEnum extends Enum {

    /**
     * Layer based on raster (imagery) data
     * @type {LayerTypeEnum}
     */
    static RASTER = new LayerTypeEnum('RASTER');

    /**
     * Layer based on vector (geometry plus metadata) data
     * @type {LayerTypeEnum}
     */
    static VECTOR = new LayerTypeEnum('VECTOR');

    static getForCode(code) {
        const forCode = Enum._getForCode(LayerTypeEnum, code);
        return forCode || this.RASTER;
    }

    static exists(code) {
        return Enum._exists(LayerTypeEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(LayerTypeEnum)
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(LayerTypeEnum)
    }
}

/**
 * Layer data retrieval protocol
 * @extends Enum
 * @hideconstructor
 */
class ProtocolEnum extends Enum {
    /**
     *
     * @type {ProtocolEnum}
     */
    static WMS = new ProtocolEnum('WMS');
    /**
     *
     * @type {ProtocolEnum}
     */
    static WMS_SINGLE_IMAGE = new ProtocolEnum('WMS_SINGLE_IMAGE');
    /**
     *
     * @type {ProtocolEnum}
     */
    static WFS = new ProtocolEnum('WFS');

    static getForCode(code) {
        return Enum._getForCode(ProtocolEnum, code);
    }

    static exists(code) {
        return Enum._exists(ProtocolEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(ProtocolEnum)
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(ProtocolEnum)
    }
}

/**
 * Possible periodicities for indicator layers
 * @extends LocalizedEnum
 * @hideconstructor
 */
class PeriodicityEnum extends LocalizedEnum {

    /**
     *
     * @type {PeriodicityEnum}
     */
    static LATEST = new PeriodicityEnum('l','enum.periodicity.l');

    /**
     *
     * @type {PeriodicityEnum}
     */
    static YEARLY = new PeriodicityEnum('y','enum.periodicity.y');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static MONTHLY = new PeriodicityEnum('m','enum.periodicity.m');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static MONTHLY_FORECAST = new PeriodicityEnum('f','enum.periodicity.f');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static MONTHLY_UPDATED_TEN_DAILY = new PeriodicityEnum('n','enum.periodicity.n');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static TEN_DAILY = new PeriodicityEnum('t','enum.periodicity.t');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static TEN_DAILY_YEARLESS = new PeriodicityEnum('u','enum.periodicity.tny');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static TEN_DAILY_YEARLESS_30DAY = new PeriodicityEnum('o','enum.periodicity.tny30day');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static DAILY_YEARLESS = new PeriodicityEnum('e','enum.periodicity.dy');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static WEEKLY = new PeriodicityEnum('w','enum.periodicity.w');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static ISO_WEEKLY = new PeriodicityEnum('i','enum.periodicity.i');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static DAILY = new PeriodicityEnum('d','enum.periodicity.d');
    /**
     *
     * @type {PeriodicityEnum}
     */
    static CUSTOM = new PeriodicityEnum('c','enum.periodicity.c');

    static getForCode(code) {
        return Enum._getForCode(PeriodicityEnum, code);
    }

    static exists(code) {
        return Enum._exists(PeriodicityEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(PeriodicityEnum)
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(PeriodicityEnum)
    }
}

/**
 * @extends LocalizedEnum
 * @hideconstructor
 */
class TimeScaleEnum extends LocalizedEnum {

    /**
     * 1 month
     * @type {TimeScaleEnum}
     */
    static _1 = new TimeScaleEnum('01', 'enum.timescale.01');

    /**
     * 3 months
     * @type {TimeScaleEnum}
     */
    static _3 = new TimeScaleEnum('03', 'enum.timescale.03');

    /**
     * 6 months
     * @type {TimeScaleEnum}
     */
    static _6 = new TimeScaleEnum('06', 'enum.timescale.06');

    /**
     * 9 months
     * @type {TimeScaleEnum}
     */
    static _9 = new TimeScaleEnum('09', 'enum.timescale.09');

    /**
     * 12 months
     * @type {TimeScaleEnum}
     */
    static _12 = new TimeScaleEnum('12', 'enum.timescale.12');

    /**
     * 24 months
     * @type {TimeScaleEnum}
     */
    static _24 = new TimeScaleEnum('24', 'enum.timescale.24');

    /**
     * 48 months
     * @type {TimeScaleEnum}
     */
    static _48 = new TimeScaleEnum('48', 'enum.timescale.48');

    static getForCode(code) {
        const forCode = Enum._getForCode(TimeScaleEnum, code);
        return forCode || this._3;
    }

    static exists(code) {
        return Enum._exists(TimeScaleEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(TimeScaleEnum)
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(TimeScaleEnum)
    }
}

/**
 * Type of device on which a layer/tool should be visible
 * @extends Enum
 * @hideconstructor
 */
class DeviceVisibilityEnum extends Enum {
    /**
     *
     * @type {DeviceVisibilityEnum}
     */
    static DESKTOP = new DeviceVisibilityEnum('DESKTOP');
    /**
     *
     * @type {DeviceVisibilityEnum}
     */
    static MOBILE = new DeviceVisibilityEnum('MOBILE');
    /**
     *
     * @type {DeviceVisibilityEnum}
     */
    static BOTH = new DeviceVisibilityEnum('BOTH');

    static getForCode(code) {
        const forCode = Enum._getForCode(DeviceVisibilityEnum, code);
        return forCode || this.BOTH;
    }

    static exists(code) {
        return Enum._exists(DeviceVisibilityEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(DeviceVisibilityEnum)
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(DeviceVisibilityEnum)
    }
}

/**
 * Types of geometries
 * @extends Enum
 * @hideconstructor
 */
class GeometryTypeEnum extends Enum {
    /**
     *
     * @type {GeometryTypeEnum}
     */
    static POINT = new GeometryTypeEnum('POINT');
    /**
     *
     * @type {GeometryTypeEnum}
     */
    static LINE = new GeometryTypeEnum('LINE');
    /**
     *
     * @type {GeometryTypeEnum}
     */
    static POLYGON = new GeometryTypeEnum('POLYGON');

    static getForCode(code) {
        return Enum._getForCode(GeometryTypeEnum, code);
    }

    static exists(code) {
        return Enum._exists(GeometryTypeEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(GeometryTypeEnum)
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(GeometryTypeEnum)
    }
}

/**
 * Possible fill types
 * @extends Enum
 * @hideconstructor
 */
class FillPatternTypeEnum extends Enum {
    /**
     *
     * @type {FillPatternTypeEnum}
     */
    static HORIZONTAL = new FillPatternTypeEnum('HORIZONTAL');
    /**
     *
     * @type {FillPatternTypeEnum}
     */
    static VERTICAL = new FillPatternTypeEnum('VERTICAL');
    /**
     *
     * @type {FillPatternTypeEnum}
     */
    static CROSS = new FillPatternTypeEnum('CROSS');
    /**
     *
     * @type {FillPatternTypeEnum}
     */
    static DIAGONAL = new FillPatternTypeEnum('DIAGONAL');
    /**
     *
     * @type {FillPatternTypeEnum}
     */
    static ANTI_DIAGONAL = new FillPatternTypeEnum('ANTI_DIAGONAL');
    /**
     *
     * @type {FillPatternTypeEnum}
     */
    static DIAGONAL_CROSS = new FillPatternTypeEnum('DIAGONAL_CROSS');
    /**
     *
     * @type {FillPatternTypeEnum}
     */
    static DOTTED = new FillPatternTypeEnum('DOTTED');

    static getForCode(code) {
        return Enum._getForCode(FillPatternTypeEnum, code);
    }

    static exists(code) {
        return Enum._exists(FillPatternTypeEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(FillPatternTypeEnum);
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(FillPatternTypeEnum);
    }
}

/**
 * Possible line types
 * @extends Enum
 * @hideconstructor
 */
class LinePatternTypeEnum extends Enum {

    /**
     *
     * @type {LinePatternTypeEnum}
     */
    static DOTTED = new LinePatternTypeEnum('DOTTED');

    /**
     *
     * @type {LinePatternTypeEnum}
     */
    static DASHED = new LinePatternTypeEnum('DASHED');

    static getForCode(code) {
        return Enum._getForCode(LinePatternTypeEnum, code);
    }

    static exists(code) {
        return Enum._exists(LinePatternTypeEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(LinePatternTypeEnum);
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(LinePatternTypeEnum);
    }
}

/**
 * Possible style rule types
 * @extends Enum
 * @hideconstructor
 */
class StyleRuleTypeEnum extends Enum {

    /**
     * Discrete styles expect a value attribute and will paint with a color by matching the value in a bucket defined with min/max values and a color
     * @type {StyleRuleTypeEnum}
     */
    static DISCRETE = new StyleRuleTypeEnum('DISCRETE');

    /**
     * Continuous styles expect a value attribute and will paint with a color in a continuum between min and max values/colors
     * @type {StyleRuleTypeEnum}
     */
    static CONTINUOUS = new StyleRuleTypeEnum('CONTINUOUS');

    /**
     * Classified styles expect an attribute that classifies features
     * @type {StyleRuleTypeEnum}
     */
    static CLASSIFIED = new StyleRuleTypeEnum('CLASSIFIED');

    /**
     * A simple style (color fill, stroke, etc.)
     * @type {StyleRuleTypeEnum}
     */
    static SIMPLE = new StyleRuleTypeEnum('SIMPLE');

    /**
     * A simple style (color fill, stroke, etc.), for features that don't have data
     * @type {StyleRuleTypeEnum}
     */
    static NODATA = new StyleRuleTypeEnum('NODATA');

    /**
     * A simple style (color fill, stroke, etc.), for clustered features
     * @type {StyleRuleTypeEnum}
     */
    static CLUSTER = new StyleRuleTypeEnum('CLUSTER');

    /**
     * Load an image as legend (only for legends)
     * @type {StyleRuleTypeEnum}
     */
    static EXTERNAL_IMAGE = new StyleRuleTypeEnum('EXTERNAL_IMAGE');

    /**
     * Load an image as legend, depending on the map extent (only for legends)
     * @type {StyleRuleTypeEnum}
     */
    static EXTERNAL_DYNAMIC_IMAGE = new StyleRuleTypeEnum('EXTERNAL_DYNAMIC_IMAGE');

    static getForCode(code) {
        return Enum._getForCode(StyleRuleTypeEnum, code);
    }

    static exists(code) {
        return Enum._exists(StyleRuleTypeEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(StyleRuleTypeEnum);
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(StyleRuleTypeEnum);
    }
}

/**
 * Describe the preferred position of the labels in the legend
 * Applicable to StyleRuleTypeEnum.DISCRETE, StyleRuleTypeEnum.CLASSIFIED
 * @extends Enum
 * @hideconstructor
 */
class LabelsPositionEnum extends Enum {
    static TOP = new LabelsPositionEnum('TOP');
    static BOTTOM = new LabelsPositionEnum('BOTTOM');
    static ALTERNATE = new LabelsPositionEnum('ALTERNATE');

    static getForCode(code) {
        return Enum._getForCode(LabelsPositionEnum, code);
    }

    static exists(code) {
        return Enum._exists(LabelsPositionEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(LabelsPositionEnum);
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(LabelsPositionEnum);
    }
}

/**
 * Describe the preferred orientation of the legend elements
 * Applicable to StyleRuleTypeEnum.CLASSIFIED
 * @extends Enum
 * @hideconstructor
 */
class LegendOrientationEnum extends Enum {
    static VERTICAL = new LabelsPositionEnum('VERTICAL');
    static HOIZONTAL = new LabelsPositionEnum('HOIZONTAL');

    static getForCode(code) {
        return Enum._getForCode(LegendOrientationEnum, code);
    }

    static exists(code) {
        return Enum._exists(LegendOrientationEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(LegendOrientationEnum);
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(LegendOrientationEnum);
    }
}

/**
 * Describe the type of chart for a layer
 * @extends Enum
 * @hideconstructor
 */
class IndicatorChartTypeEnum extends Enum {
    static BARS = new IndicatorChartTypeEnum('BARS');
    static BARS_WITH_LINE = new IndicatorChartTypeEnum('BARS_WITH_LINE');
    static BARS_WITH_LINE_AND_ACCUMULATION = new IndicatorChartTypeEnum('BARS_WITH_LINE_AND_ACCUMULATION');
    static STACKED_BARS = new IndicatorChartTypeEnum('STACKED_BARS');

    static getForCode(code) {
        return Enum._getForCode(IndicatorChartTypeEnum, code);
    }

    static exists(code) {
        return Enum._exists(IndicatorChartTypeEnum, code);
    }

    static getPossibleCodes() {
        return Enum._getPossibleCodes(IndicatorChartTypeEnum);
    }

    static getPossibleValues() {
        return Enum._getPossibleValues(IndicatorChartTypeEnum);
    }
}

export {Enum};
export {LocalizedEnum};
export {DeviceVisibilityEnum};
export {TimeScaleEnum};
export {PeriodicityEnum};
export {ProtocolEnum};
export {LayerTypeEnum};
export {LayerGroupTypeEnum};
export {GeometryTypeEnum};
export {StyleRuleTypeEnum};
export {LabelsPositionEnum};
export {LegendOrientationEnum};
export {FillPatternTypeEnum};
export {LinePatternTypeEnum};
export {IndicatorChartTypeEnum};